import { z } from 'zod';
import { FINDING_SEVERITIES } from '../review/check-codes.js';
/**
 * `reviewer` (spec 80 §4.7, S80-6): checks the current version of a content
 * item against the verified knowledge, language, brand tone, repetition,
 * promotional tone, text length and media usage, and writes findings through
 * module 50's `ReviewerFindingsSink` onto exactly the version it reviewed. It
 * never changes content or status: findings only.
 */
export const REVIEWER_FINDING_CODES = [
    'unsupported_claim',
    'fact_mismatch',
    'language_quality',
    'brand_tone',
    'repetition',
    'too_promotional',
    'text_too_long',
    'media_overused',
    'glossary_violation',
    /** S200-4 (prompt v4 on): the text reads as machine-written; also found deterministically from the AI-cliche list (spec 80 section 4.15). */
    'sounds_generated',
    /** S280-4 (every prompt version): a forbidden phrase of the business is in the text; found deterministically only, the model may not propose it (spec 80 section 4.18). */
    'forbidden_phrase',
    /** S280-8 (prompt v6 on, copy quality round 2, spec 80 section 4.18): the first two always block, the other three are warnings. */
    'invented_experience',
    'unconfirmed_current_state',
    'copy_too_long',
    'emoji_repetition',
    'too_many_facts',
    'other',
];
/**
 * Codes that are always `blocking`, whatever the model proposes (spec 80 §4.7).
 * Spec 80 §9 Q8 interim answer (2026-09-29, agent): only the two fact codes
 * block (S280-8 adds invented experience and unconfirmed current state); every other code is capped at `warning`. This list is the one place
 * to change if the human decides otherwise.
 */
export const BLOCKING_REVIEWER_CODES = ['unsupported_claim', 'fact_mismatch', 'invented_experience', 'unconfirmed_current_state'];
/** Content states a review can run on: text exists and the item is not decided or archived. */
export const AI_REVIEWABLE_CONTENT_STATUSES = ['draft', 'changes_requested', 'in_review'];
export const REVIEWER_LIMITS = {
    maxFindings: 30,
    maxMessageLength: 500,
    maxKnowledgeSources: 10,
    /** Captions of the property's last publications in the context (repetition check). */
    recentCaptions: 20,
};
export const reviewerInputSchema = z
    .object({
    content_item_id: z.uuid(),
    base_version_id: z.uuid(),
})
    .strict();
export const reviewerFindingSchema = z
    .object({
    code: z.enum(REVIEWER_FINDING_CODES),
    severity: z.enum(FINDING_SEVERITIES),
    message: z.string().min(1).max(REVIEWER_LIMITS.maxMessageLength),
    /** The localization's language the finding is about; `null` = the whole item. */
    language: z.string().min(1).max(35).nullable(),
    /** Knowledge items a fact finding relies on; only IDs from the context. */
    knowledge_item_ids: z.array(z.string()).max(REVIEWER_LIMITS.maxKnowledgeSources),
})
    .strict();
export const reviewerOutputSchema = z
    .object({
    findings: z.array(reviewerFindingSchema).max(REVIEWER_LIMITS.maxFindings),
})
    .strict();
/**
 * The stored severity (AC-80-037): `unsupported_claim` and `fact_mismatch`
 * are always `blocking`; for every other code a proposed `blocking` is capped
 * to `warning`, `info` and `warning` stay.
 */
export function reviewerSeverity(code, proposed) {
    if (BLOCKING_REVIEWER_CODES.includes(code))
        return 'blocking';
    return proposed === 'blocking' ? 'warning' : proposed;
}
//# sourceMappingURL=reviewer.js.map